// src/components/CountrySelect.js
// Select de pays - natif sur mobile, react-select sur desktop
import { useMemo, useState, useEffect } from 'react'
import Select from 'react-select'
import { CFormSelect } from '@coreui/react'
import countries from 'world-countries'

// Pays francophones et européens à mettre en priorité
const priorityCountries = [
  'FR', // France
  'BE', // Belgique
  'CH', // Suisse
  'CA', // Canada
  'LU', // Luxembourg
  'MC', // Monaco
]

const CountrySelect = ({
  value,
  onChange,
  placeholder = 'Sélectionnez un pays',
  size = 'md',
  className = '',
  invalid = false,
  disabled = false,
  isClearable = true,
  native = false, // Forcer le mode natif
  ...props
}) => {
  // Select natif uniquement sur petit écran (tactile). On NE bascule PAS en
  // natif juste parce qu'on est en PWA : sur écran large (PC / iPad paysage)
  // la PWA doit afficher le même react-select riche que le web. Le critère est
  // donc la LARGEUR, comme sur le web — pas le mode standalone.
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Générer les options triées
  const countryOptions = useMemo(() => {
    const allOptions = countries.map((country) => ({
      value: country.translations?.fra?.common || country.name.common,
      code: country.cca2,
      label: country.translations?.fra?.common || country.name.common,
      searchLabel: `${country.translations?.fra?.common || ''} ${country.name.common}`.toLowerCase(),
    }))

    // Trier : pays prioritaires en premier, puis alphabétique
    return allOptions.sort((a, b) => {
      const aIsPriority = priorityCountries.includes(a.code)
      const bIsPriority = priorityCountries.includes(b.code)

      if (aIsPriority && !bIsPriority) return -1
      if (!aIsPriority && bIsPriority) return 1
      if (aIsPriority && bIsPriority) {
        return priorityCountries.indexOf(a.code) - priorityCountries.indexOf(b.code)
      }
      return a.label.localeCompare(b.label, 'fr')
    })
  }, [])

  // Mode natif (mobile/PWA) - select HTML simple
  if (native || isMobile) {
    return (
      <CFormSelect
        value={value || ''}
        onChange={onChange}
        size={size}
        className={className}
        disabled={disabled}
        invalid={invalid}
        {...props}
      >
        <option value="">{placeholder}</option>
        {countryOptions.map((country) => (
          <option key={country.code} value={country.value}>
            {country.label}
          </option>
        ))}
      </CFormSelect>
    )
  }

  // Mode desktop - react-select avec autocomplétion
  const selectedOption = countryOptions.find((opt) => opt.value === value) || null

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: size === 'sm' ? '31px' : '38px',
      // Hauteur FIXE en sm : sans ça le contrôle dépasse la hauteur des
      // CFormInput sm voisins (filtre pays « trop grand » dans l'annuaire).
      height: size === 'sm' ? '31px' : undefined,
      borderColor: invalid ? '#e55353' : state.isFocused ? '#321fdb' : '#d8dbe0',
      boxShadow: invalid
        ? '0 0 0 0.2rem rgba(229, 83, 83, 0.25)'
        : state.isFocused
          ? '0 0 0 0.2rem rgba(50, 31, 219, 0.25)'
          : null,
      '&:hover': {
        borderColor: invalid ? '#e55353' : '#321fdb',
      },
      fontSize: size === 'sm' ? '0.875rem' : '1rem',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: size === 'sm' ? '0 8px' : '2px 8px',
      height: size === 'sm' ? '31px' : undefined,
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: size === 'sm' ? '31px' : undefined,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: size === 'sm' ? '4px' : '8px',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: size === 'sm' ? '4px' : '8px',
    }),
    option: (provided, state) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      backgroundColor: state.isSelected
        ? '#321fdb'
        : state.isFocused
          ? '#f0f0ff'
          : 'transparent',
      color: state.isSelected ? 'white' : '#333',
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    // Quand le select est rendu via un portal (ex: dans une modale, en passant
    // menuPortalTarget={document.body}), garantir que le menu passe au-dessus
    // de la modale (sinon zIndex par défaut du portal = 1 → caché derrière).
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  }

  const formatOptionLabel = ({ label, code }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <img
        src={`https://flagcdn.com/20x15/${code?.toLowerCase()}.png`}
        alt=""
        style={{
          width: '20px',
          height: '15px',
          objectFit: 'cover',
          borderRadius: '2px',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
        onError={(e) => {
          e.target.style.display = 'none'
        }}
      />
      <span>{label}</span>
    </div>
  )

  const filterOption = (option, inputValue) => {
    if (!inputValue) return true
    return option.data.searchLabel.includes(inputValue.toLowerCase())
  }

  return (
    <Select
      value={selectedOption}
      onChange={(selected) => {
        const syntheticEvent = {
          target: {
            value: selected ? selected.value : '',
          },
        }
        onChange(syntheticEvent)
      }}
      options={countryOptions}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={disabled}
      isSearchable
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      styles={customStyles}
      className={className}
      classNamePrefix="country-select"
      noOptionsMessage={() => 'Aucun pays trouvé'}
      loadingMessage={() => 'Chargement...'}
      {...props}
    />
  )
}

export default CountrySelect
